import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Booking } from "@/mocks/admin/bookings";
import { supabase } from "@/lib/supabase";
import { useCrew } from "@/hooks/admin/useAdminData";

type Props = {
  booking: Booking | null;
  onClose: () => void;
  onUpdated?: () => void;
};

type SaveState = "idle" | "saving" | "success" | "error";

const STAGE_FLOW: Booking["stage"][] = ["new", "quoted", "confirmed", "scheduled"];

const STAGE_LABEL: Record<Booking["stage"], string> = {
  new: "New Request",
  quoted: "Quoted",
  confirmed: "Confirmed",
  scheduled: "Scheduled",
};

const STAGE_DOT: Record<Booking["stage"], string> = {
  new: "bg-amber-500",
  quoted: "bg-indigo-500",
  confirmed: "bg-brand-500",
  scheduled: "bg-emerald-500",
};

const TRUCKS = ["T-201 (16ft)", "T-202 (20ft)", "T-203 (26ft)", "T-204 (26ft Box)"];

export default function BookingDrawer({ booking, onClose, onUpdated }: Props) {
  const navigate = useNavigate();
  const { data: crew } = useCrew();

  const [quote, setQuote] = useState("");
  const [deposit, setDeposit] = useState("");
  const [quoteNote, setQuoteNote] = useState("");
  const [depositReceived, setDepositReceived] = useState(false);
  const [leadCrew, setLeadCrew] = useState("");
  const [crewCount, setCrewCount] = useState(3);
  const [truck, setTruck] = useState(TRUCKS[1]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Reset state whenever a new booking is opened
  useEffect(() => {
    if (!booking) return;
    setQuote(booking.estimatedQuote ? String(booking.estimatedQuote) : "");
    setDeposit(
      booking.depositRequired
        ? String(booking.depositRequired)
        : booking.estimatedQuote
        ? String(Math.round(booking.estimatedQuote * 0.2))
        : "",
    );
    setQuoteNote("");
    setDepositReceived(booking.depositPaid);
    setLeadCrew("");
    setCrewCount(3);
    setTruck(TRUCKS[1]);
    setErrorMsg(null);
    setSaveState("idle");
  }, [booking?.id]);

  const leadOptions = useMemo(
    () =>
      crew
        .filter((c) => c.status === "active" && c.role.toLowerCase().includes("lead"))
        .map((c) => c.name),
    [crew],
  );

  if (!booking) {
    return (
      <>
        <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm opacity-0 pointer-events-none transition-opacity duration-300" />
        <aside className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-cream-50 border-l border-ink-100 transition-transform duration-300 flex flex-col translate-x-full" />
      </>
    );
  }

  const stageIndex = STAGE_FLOW.indexOf(booking.stage);

  const flash = (state: SaveState) => {
    setSaveState(state);
    if (state === "success") {
      setTimeout(() => setSaveState("idle"), 1400);
    }
  };

  const handleSendQuote = async () => {
    const quoteNum = Number(quote);
    const depositNum = Number(deposit);
    if (!quoteNum || quoteNum <= 0) {
      setErrorMsg("Enter a valid quote amount.");
      return;
    }
    if (!depositNum || depositNum <= 0) {
      setErrorMsg("Enter a valid deposit amount.");
      return;
    }
    setErrorMsg(null);
    setSaveState("saving");
    const { error } = await supabase
      .from("bookings")
      .update({
        estimated_quote: quoteNum,
        deposit_required: depositNum,
        stage: "quoted",
      })
      .eq("id", booking.id);
    if (error) {
      setErrorMsg(error.message);
      flash("error");
      return;
    }
    flash("success");
    onUpdated?.();
  };

  const handleConfirmDeposit = async () => {
    setErrorMsg(null);
    setSaveState("saving");
    const { error } = await supabase
      .from("bookings")
      .update({
        deposit_paid: true,
        stage: "confirmed",
      })
      .eq("id", booking.id);
    if (error) {
      setErrorMsg(error.message);
      flash("error");
      return;
    }
    flash("success");
    onUpdated?.();
  };

  const handleScheduleProject = async () => {
    if (!leadCrew) {
      setErrorMsg("Assign a lead mover to schedule this job.");
      return;
    }
    if (!truck) {
      setErrorMsg("Pick a truck for this job.");
      return;
    }
    setErrorMsg(null);
    setSaveState("saving");

    // Generate next project id
    const { data: lastProject } = await supabase
      .from("moving_projects")
      .select("id")
      .order("id", { ascending: false })
      .limit(1)
      .maybeSingle();

    let nextNum = 2051;
    if (lastProject?.id) {
      const m = /MV-(\d+)/.exec(lastProject.id);
      if (m) nextNum = parseInt(m[1], 10) + 1;
    }
    const newProjectId = `MV-${nextNum}`;

    const { error: insertErr } = await supabase.from("moving_projects").insert({
      id: newProjectId,
      customer_name: booking.customer,
      customer_initials: booking.initials,
      customer_color: booking.color,
      move_type: booking.moveType,
      move_date: booking.preferredDate,
      move_time: booking.preferredTime,
      from_city: booking.fromCity,
      to_city: booking.toCity,
      truck,
      crew_count: crewCount,
      lead_crew: leadCrew,
      status: "scheduled",
      total: booking.estimatedQuote || 0,
      deposit_paid: true,
      notes: booking.notes,
    });

    if (insertErr) {
      setErrorMsg(insertErr.message);
      flash("error");
      return;
    }

    const { error: updErr } = await supabase
      .from("bookings")
      .update({
        stage: "scheduled",
        linked_project_id: newProjectId,
      })
      .eq("id", booking.id);

    if (updErr) {
      setErrorMsg(updErr.message);
      flash("error");
      return;
    }

    flash("success");
    onUpdated?.();
    setTimeout(() => {
      onClose();
      navigate(`/projects/${newProjectId}`);
    }, 700);
  };

  const handleReject = async () => {
    setErrorMsg(null);
    setSaveState("saving");
    const { error } = await supabase.from("bookings").delete().eq("id", booking.id);
    if (error) {
      setErrorMsg(error.message);
      flash("error");
      return;
    }
    flash("success");
    onUpdated?.();
    setTimeout(onClose, 500);
  };

  const handleViewProject = () => {
    onClose();
    // booking row may have linked_project_id; we navigate by best-effort
    const linked = (booking as unknown as { linked_project_id?: string }).linked_project_id;
    if (linked) navigate(`/projects/${linked}`);
    else navigate("/projects");
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm transition-opacity duration-300 opacity-100"
        onClick={onClose}
      />

      {/* Drawer */}
      <aside className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-cream-50 border-l border-ink-100 transition-transform duration-300 flex flex-col translate-x-0">
        {/* Header */}
        <div className="px-5 py-4 bg-white border-b border-ink-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-bold text-ink-400">{booking.id}</span>
            <span className="text-[10px] font-semibold text-ink-500 bg-cream-100 px-2 py-0.5 rounded-full">
              {booking.source}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {/* Stage stepper */}
          <div className="px-5 pt-4 pb-2 bg-white">
            <div className="flex items-center gap-1">
              {STAGE_FLOW.map((s, i) => {
                const active = i <= stageIndex;
                const current = i === stageIndex;
                return (
                  <div key={s} className="flex items-center flex-1 last:flex-initial">
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                          active
                            ? `${STAGE_DOT[s]} text-white`
                            : "bg-cream-100 text-ink-400"
                        } ${current ? "ring-2 ring-offset-2 ring-ink-900/20" : ""}`}
                      >
                        {active && i < stageIndex ? (
                          <i className="ri-check-line"></i>
                        ) : (
                          i + 1
                        )}
                      </div>
                      <span
                        className={`text-[9px] font-bold whitespace-nowrap ${
                          current ? "text-ink-900" : "text-ink-400"
                        }`}
                      >
                        {STAGE_LABEL[s]}
                      </span>
                    </div>
                    {i < STAGE_FLOW.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-1 -mt-3 ${
                          i < stageIndex ? "bg-ink-900" : "bg-ink-100"
                        }`}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Customer */}
          <div className="px-5 py-5 bg-white border-b border-ink-100">
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-14 h-14 rounded-full ${booking.color} text-white text-base font-bold flex items-center justify-center`}
              >
                {booking.initials}
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-xl font-bold text-ink-900 truncate">
                  {booking.customer}
                </h2>
                <div className="text-xs text-ink-500">{booking.moveType}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 text-sm">
              <a
                href={`tel:${booking.phone}`}
                className="flex items-center gap-2.5 text-ink-700 hover:text-brand-700 cursor-pointer"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-cream-100">
                  <i className="ri-phone-line text-sm"></i>
                </span>
                {booking.phone}
              </a>
              <a
                href={`mailto:${booking.email}`}
                className="flex items-center gap-2.5 text-ink-700 hover:text-brand-700 cursor-pointer truncate"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-cream-100">
                  <i className="ri-mail-line text-sm"></i>
                </span>
                <span className="truncate">{booking.email}</span>
              </a>
            </div>
          </div>

          {/* Move details */}
          <div className="px-5 py-5 space-y-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400 mb-2">
                Move details
              </div>
              <div className="bg-white border border-ink-100 rounded-lg divide-y divide-ink-100">
                <Row label="Preferred date" value={`${booking.preferredDate} · ${booking.preferredTime}`} />
                <Row label="Origin" value={booking.fromCity} />
                <Row label="Destination" value={booking.toCity} />
                <Row label="Size" value={booking.rooms} />
              </div>
            </div>

            {/* Stage-specific panel */}
            {booking.stage === "new" && (
              <Section title="Step 1 · Prepare the quote">
                <div className="text-xs text-ink-500 mb-3">
                  Review the request and build an estimate. Once you send it, the customer gets a quote and the booking moves to the next stage.
                </div>

                <Field label="Estimated quote ($)">
                  <CurrencyInput value={quote} onChange={setQuote} placeholder="e.g. 1800" />
                </Field>
                <Field label="Deposit required ($)">
                  <CurrencyInput value={deposit} onChange={setDeposit} placeholder="20% suggested" />
                </Field>
                <Field label="Quote note (optional)">
                  <textarea
                    rows={3}
                    value={quoteNote}
                    onChange={(e) => setQuoteNote(e.target.value)}
                    placeholder="Anything the customer should know — what's included, timing, exclusions…"
                    className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white resize-none leading-relaxed"
                  />
                </Field>

                {quote && deposit && (
                  <div className="bg-cream-100 border border-ink-100 rounded-md p-3 text-xs text-ink-600 leading-relaxed">
                    <div className="font-bold text-ink-900 mb-1">Preview</div>
                    Customer pays{" "}
                    <span className="font-bold text-ink-900 tabular-nums">
                      ${Number(deposit).toLocaleString()}
                    </span>{" "}
                    deposit to confirm. Balance{" "}
                    <span className="font-bold text-ink-900 tabular-nums">
                      ${(Number(quote) - Number(deposit)).toLocaleString()}
                    </span>{" "}
                    due on move day.
                  </div>
                )}
              </Section>
            )}

            {booking.stage === "quoted" && (
              <Section title="Step 2 · Awaiting deposit">
                <div className="bg-white border border-ink-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-ink-500">Quote sent</span>
                    <span className="font-display text-xl font-bold text-ink-900 tabular-nums">
                      ${booking.estimatedQuote?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-ink-100">
                    <span className="text-sm text-ink-500">Deposit required</span>
                    <span className="text-sm font-bold text-ink-900 tabular-nums">
                      ${(booking.depositRequired || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={depositReceived}
                    onChange={(e) => setDepositReceived(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-600 cursor-pointer"
                  />
                  <div>
                    <div className="text-sm font-semibold text-ink-900">
                      Deposit received
                    </div>
                    <div className="text-[11px] text-ink-500">
                      Tick this once the customer has paid — we'll move the booking to Confirmed.
                    </div>
                  </div>
                </label>
              </Section>
            )}

            {booking.stage === "confirmed" && (
              <Section title="Step 3 · Assign crew & schedule">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-3 flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-0.5"></i>
                  <div className="text-[11px] text-emerald-800 leading-relaxed">
                    Deposit{" "}
                    <span className="font-bold tabular-nums">
                      ${(booking.depositRequired || 0).toLocaleString()}
                    </span>{" "}
                    received. Pick a lead, crew size, and truck to convert this into a scheduled project.
                  </div>
                </div>

                <Field label="Lead mover">
                  <select
                    value={leadCrew}
                    onChange={(e) => setLeadCrew(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white cursor-pointer"
                  >
                    <option value="">Pick a lead…</option>
                    {leadOptions.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Crew size">
                  <div className="inline-flex items-center gap-2 bg-white border border-ink-200 rounded-md">
                    <button
                      type="button"
                      onClick={() => setCrewCount((n) => Math.max(2, n - 1))}
                      className="w-9 h-9 flex items-center justify-center text-ink-700 hover:bg-cream-100 rounded-l-md cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-8 text-center text-sm font-bold text-ink-900 tabular-nums">
                      {crewCount}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCrewCount((n) => Math.min(8, n + 1))}
                      className="w-9 h-9 flex items-center justify-center text-ink-700 hover:bg-cream-100 rounded-r-md cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                    <span className="text-[10px] text-ink-400 pr-3">movers</span>
                  </div>
                </Field>

                <Field label="Truck">
                  <select
                    value={truck}
                    onChange={(e) => setTruck(e.target.value)}
                    className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white cursor-pointer"
                  >
                    {TRUCKS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </Section>
            )}

            {booking.stage === "scheduled" && (
              <Section title="Scheduled">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center">
                  <div className="w-10 h-10 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-2">
                    <i className="ri-check-double-line text-lg"></i>
                  </div>
                  <div className="text-sm font-bold text-ink-900 mb-1">
                    This booking is now a live project
                  </div>
                  <div className="text-[11px] text-ink-600">
                    Crew assigned and on the schedule. Manage from the project page.
                  </div>
                </div>
              </Section>
            )}

            {/* Notes */}
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400 mb-2">
                Customer notes
              </div>
              <div className="bg-white border border-ink-100 rounded-lg p-4 text-sm text-ink-700 leading-relaxed">
                {booking.notes}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-white border-t border-ink-100 shrink-0">
          {errorMsg && (
            <div className="mb-2 bg-rose-50 border border-rose-200 text-rose-700 text-[11px] rounded-md px-2.5 py-1.5">
              <span className="font-semibold">Heads up:</span> {errorMsg}
            </div>
          )}

          <div className="flex items-center gap-2">
            {booking.stage === "new" && (
              <>
                <button
                  onClick={handleReject}
                  disabled={saveState === "saving"}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className="ri-close-circle-line"></i>
                  Reject
                </button>
                <button
                  onClick={handleSendQuote}
                  disabled={saveState === "saving" || saveState === "success"}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold text-white cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed ${
                    saveState === "success"
                      ? "bg-emerald-500"
                      : "bg-ink-900 hover:bg-ink-800"
                  }`}
                >
                  <i
                    className={
                      saveState === "saving"
                        ? "ri-loader-4-line animate-spin"
                        : saveState === "success"
                        ? "ri-check-double-line"
                        : "ri-mail-send-line"
                    }
                  ></i>
                  {saveState === "saving"
                    ? "Sending…"
                    : saveState === "success"
                    ? "Quote sent"
                    : "Send Quote"}
                </button>
              </>
            )}

            {booking.stage === "quoted" && (
              <>
                <button
                  onClick={handleReject}
                  disabled={saveState === "saving"}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <i className="ri-close-circle-line"></i>
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeposit}
                  disabled={
                    !depositReceived || saveState === "saving" || saveState === "success"
                  }
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold text-white cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    saveState === "success"
                      ? "bg-emerald-500"
                      : "bg-ink-900 hover:bg-ink-800"
                  }`}
                >
                  <i
                    className={
                      saveState === "saving"
                        ? "ri-loader-4-line animate-spin"
                        : saveState === "success"
                        ? "ri-check-double-line"
                        : "ri-bank-card-line"
                    }
                  ></i>
                  {saveState === "saving"
                    ? "Confirming…"
                    : saveState === "success"
                    ? "Confirmed"
                    : "Confirm Deposit"}
                </button>
              </>
            )}

            {booking.stage === "confirmed" && (
              <button
                onClick={handleScheduleProject}
                disabled={saveState === "saving" || saveState === "success"}
                className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-bold text-white cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed ${
                  saveState === "success"
                    ? "bg-emerald-500"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                <i
                  className={
                    saveState === "saving"
                      ? "ri-loader-4-line animate-spin"
                      : saveState === "success"
                      ? "ri-check-double-line"
                      : "ri-truck-line"
                  }
                ></i>
                {saveState === "saving"
                  ? "Creating project…"
                  : saveState === "success"
                  ? "Scheduled!"
                  : "Schedule Project"}
              </button>
            )}

            {booking.stage === "scheduled" && (
              <button
                onClick={handleViewProject}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-bold text-white bg-ink-900 hover:bg-ink-800 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Open project
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-3 flex justify-between text-sm">
      <span className="text-ink-500">{label}</span>
      <span className="font-semibold text-ink-900 text-right">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400 mb-2">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-1 block">
        {label}
      </label>
      {children}
    </div>
  );
}

function CurrencyInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">$</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm pl-7 pr-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white tabular-nums"
      />
    </div>
  );
}